import { useEffect, useRef, useState } from "react";
import { useQueryData } from "hooks";
import { GET_ME } from "graphql/queries";
import { MEMBER_STATUS } from "constants";

/**
 * useMissionRoleApplicantAccess
 *
 * @param {String} applicationUuid - The UUID of the application to check access for.
 */
export default function useMissionRoleApplicantAccess({ applicationUuid }) {
  const [isValidated, setIsValidated] = useState(false);
  const [requiresPin, setrequiresPin] = useState(false);
  const { data: currentUser, loading: userLoading } = useQueryData({ queryName: GET_ME });
  const isLoggedIn = Boolean(currentUser) && !userLoading;
  const isMember = currentUser?.me?.profile?.status === MEMBER_STATUS.active;

  const intervalRef = useRef(null);

  const PREVIEW_TOKEN = `application-preview-token-${applicationUuid}`;
  const PREVIEW_EXPIRES_AT = `application-preview-expires-at-${applicationUuid}`;

  const setAccessData = (data) => {
    if (data?.token && data?.expiresAt) {
      localStorage.setItem(PREVIEW_TOKEN, data.token);
      localStorage.setItem(PREVIEW_EXPIRES_AT, data.expiresAt);

      // Refresh the page to ensure Apollo client picks up the token
      window.location.reload();
    }
  };

  const removeAccessData = () => {
    localStorage.removeItem(PREVIEW_TOKEN);
    localStorage.removeItem(PREVIEW_EXPIRES_AT);
    setIsValidated(false);
  };

  useEffect(() => {
    if ((isLoggedIn && !isMember) || userLoading) return;

    const checkTokenValidity = () => {
      const token = localStorage.getItem(PREVIEW_TOKEN);
      const expiresAt = localStorage.getItem(PREVIEW_EXPIRES_AT);
      if (!token && !expiresAt) return;

      try {
        const now = Date.now();

        if (expiresAt * 1000 <= now) {
          removeAccessData();
          return;
        } else {
          setIsValidated(true);
        }
      } catch {
        removeAccessData();
      }
    };

    checkTokenValidity(); // Immediate check
    intervalRef.current = setInterval(checkTokenValidity, 5000);

    return () => clearInterval(intervalRef.current);
  }, [isLoggedIn, userLoading]);

  useEffect(() => {
    if (userLoading) return;

    setrequiresPin((!isLoggedIn || isMember) && !isValidated);
  }, [isMember, isLoggedIn, isValidated, userLoading]);

  return {
    isLoggedIn,
    userLoading,
    setAccessData,
    currentUser,
    isValidated,
    requiresPin,
  };
}
